import React, { useEffect } from "react";

import moment, { Moment } from "moment";

import { addOrUpdateVerificationTask } from "services";

import { reverseCamelizeString, useGetAllCarbonPrograms, useGetPractices } from "utils";

import { COUNTRY_CODE } from "consts";

import { AddOrUpdateVerificationTask, VerificationTask } from "models";

import { Button, Col, DatePicker, Drawer, Form, Input, Option, Radio, Row, Select, TextArea, Message as message } from "components";

type VerificationsTaskDrawerProps = {
  task: VerificationTask | null;
  visible: boolean;
  onClose: () => void;
};

export const VerificationsTaskDrawer = ({ task, visible, onClose }: VerificationsTaskDrawerProps) => {
  const { data: practices } = useGetPractices(true);
  const { data: carbonPrograms } = useGetAllCarbonPrograms();
  const [form] = Form.useForm();
  const filterType = Form.useWatch("filterType", form);
  const hasPractices = Form.useWatch("hasPractices", form);
  const isDefaultTask = Form.useWatch("defaultTask", form);
  const practiceSlugs = Form.useWatch("practiceSlugs", form);

  const handleFinish = (v: AddOrUpdateVerificationTask & { filterType: "offering" | "program" | "none"; deadline: Moment }) => {
    addOrUpdateVerificationTask({
      documentName: v.documentName,
      description: v.description,
      offeringFilter: v.filterType === "offering" ? v.offeringFilter : null,
      programFilter: v.filterType === "program" ? v.programFilter : null,
      practiceSlugs: hasPractices ? v.practiceSlugs : [],
      id: task?.id,
      defaultTask: v.defaultTask,
      country: v.country,
      startDate: v.startDate,
      yearFilter: v.yearFilter,
      deadline: v.deadline?.format("MM-DD") ?? null,
    }).then(() => {
      message({
        content: "The gremlins accepted your sacrifice and will consume it! Nom nom nom",
        icon: "gremlin",
      });
      onClose();
    });
  };

  // Whenever a new task is selected we need to reset the initial values
  useEffect(() => {
    form.resetFields();
  }, [task, form]);

  const initialDeadline = task?.deadline ? moment(`${moment().year()}-${task?.deadline}`) : null;

  return (
    <Drawer
      destroyOnHidden={true}
      visible={visible}
      onClose={onClose}
      title="Add or update task"
      footer={
        <Button dataTestId="edit-verification-task-submit" type="primary" onClick={form.submit}>
          Submit data
        </Button>
      }
    >
      <h2 className="margin-bottom-100">Set a verification task for farmer</h2>
      <p className="paragraph-m">
        To request documents necessary for verification process set up verification tasks for farmers. In each task you can request for only one document.
        Select all practices that will we verified with this document.
      </p>
      <Form form={form} onFinish={handleFinish}>
        <Row className="paragram-m padding-top-100">
          <Col>
            {/* @ts-expect-error Select not yet in ts */}
            <Select name="country" rules={[{ required: true }]} label="Country" initialValue={task?.country}>
              {Object.entries(COUNTRY_CODE)
                .filter(
                  ([_countryName, countryCode]) =>
                    countryCode !== COUNTRY_CODE.ENGLAND && countryCode !== COUNTRY_CODE.SWITZERLAND && countryCode !== COUNTRY_CODE.ZULU
                )
                .map(([countryName, countryCode]) => (
                  <Option value={countryCode} key={countryCode}>
                    {countryName}
                  </Option>
                ))}
            </Select>
          </Col>
        </Row>
        <Row>
          <Col>
            <Input
              dataTestId="edit-verification-task-document-name"
              initialValue={task?.documentName}
              name="documentName"
              label="Document name"
              rules={[{ required: true }]}
              className="margin-bottom-100"
            />
            <TextArea
              dataTestId="edit-verification-task-description"
              initialValue={task?.description}
              name="description"
              label="Description"
              className="margin-bottom-100"
              rows={4}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <h4 className="margin-bottom-100">Should this task be sent automatically?</h4>
            <Form.Item name="defaultTask" initialValue={task?.defaultTask}>
              <Radio.Group>
                <Radio className="margin-bottom-100" value={true}>
                  Yes, send task automatically when verification process starts.
                </Radio>
                <Radio value={false}>No, this task will be sent only manually and have custom due date.</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        {isDefaultTask && (
          <>
            <Row>
              <Col>
                <DatePicker
                  dataTestId="edit-verification-task-start-date"
                  initialValue={task?.startDate ? moment(task?.startDate) : null}
                  defaultPickerValue={task?.startDate ? moment(task?.startDate) : null}
                  format={["DD.MM.YYYY"]}
                  label="Start Date"
                  name="startDate"
                  rules={[{ required: true }]}
                />
              </Col>
            </Row>

            <Row className="padding-top-100">
              <Col>
                <h4 className="margin-bottom-100">Task deadline</h4>
                <p className="paragraph-m">
                  This field sets the deadline until which the farmer must complete the task. The date will be set for each consequent year as well. If you
                  leave it empty, the deadline will be based upon the default number of days configured in the Deadline tab.
                </p>
                <DatePicker
                  dataTestId="edit-verification-task-start-date"
                  initialValue={initialDeadline}
                  defaultPickerValue={initialDeadline}
                  format={["DD.MM"]}
                  label="Deadline (every year)"
                  name="deadline"
                />
              </Col>
            </Row>
          </>
        )}

        <Row className="padding-top-100">
          <Col>
            <h4 className="margin-bottom-100">Should a program filter be applied?</h4>
            <p className="paragraph-m">
              {" "}
              Selecting "Carbon Program Based", allows you to only send this task only to farmers assigned to a specific carbon program. Selecting "Offering
              Based" allows you to send this task to any farmer assigned to a specific program type.
            </p>

            {/* @ts-expect-error Select not yet in ts */}
            <Select
              name="filterType"
              label="Filter type"
              initialValue={task?.programFilter?.length ? "program" : task?.offeringFilter ? "offering" : "none"}
              className="margin-bottom-100"
            >
              <Option value="none">None</Option>
              <Option value="program">Carbon Program Based</Option>
              <Option value="offering">Offering Based</Option>
            </Select>

            {filterType === "program" && (
              // @ts-expect-error Select not yet in ts
              <Select
                mode="tags"
                name="programFilter"
                label="Carbon Program"
                initialValue={task?.programFilter}
                className="margin-bottom-100"
                rules={[{ required: true }]}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                filterOption={(input: string, option: any) => option.children.toLowerCase().includes(input.toLowerCase())}
              >
                {carbonPrograms
                  ?.filter(({ isEnabled }) => isEnabled)
                  .map(({ id, programName }) => (
                    <Option value={id} key={id}>
                      {programName}
                    </Option>
                  ))}
              </Select>
            )}

            {filterType === "offering" && (
              // @ts-expect-error Select not yet in ts
              <Select
                name="offeringFilter"
                label="Offering Type"
                initialValue={task?.offeringFilter}
                className="margin-bottom-100"
                rules={[{ required: true }]}
              >
                <Option value="intervention">Intervention</Option>
                <Option value="inventory">Inventory</Option>
                <Option value="footprinting">Footprinting</Option>
              </Select>
            )}
          </Col>
        </Row>

        <Row className="padding-top-100">
          <Col>
            <h4 className="margin-bottom-100">Is this task verifying specific practices?</h4>
            <Form.Item name="hasPractices" initialValue={!!task?.practiceSlugs.length}>
              <Radio.Group className="margin-bottom-100">
                <Radio className="margin-bottom-100" style={{ width: "100%" }} value={true}>
                  Yes
                </Radio>
                <Radio value={false}>No, this is for general information</Radio>
              </Radio.Group>
            </Form.Item>

            {hasPractices && practices && (
              // @ts-expect-error Select not yet in ts
              <Select name="practiceSlugs" mode="tags" label="Select practices" initialValue={task?.practiceSlugs} rules={[{ required: true }]}>
                {practices
                  ?.filter(({ enabled }) => enabled)
                  .map(({ slug }) => (
                    <Option value={slug} key={slug}>
                      {reverseCamelizeString(slug)}
                    </Option>
                  ))}
              </Select>
            )}
            <p className="paragraph-s">Select all practices that are reviewed with this document.</p>
          </Col>
        </Row>

        {practiceSlugs && practiceSlugs.length === 1 && practiceSlugs[0] === "crops" && (
          <Row>
            <Col>
              <h4 className="margin-bottom-100">Should a year filter be applied?</h4>
              <p className="paragraph-m">
                {" "}
                When sending tasks for crops, if not yearly filter is applied, the task will be sent to every year the farmer has pending crop verifications in.
                Selecting "CURRENT AND RANDOM YEAR" allows you to only send this task for those two years.
              </p>
              {/* @ts-expect-error Select not yet in ts */}
              <Select name="yearFilter" label="Year Filter" initialValue={task?.yearFilter ?? null} className="margin-bottom-100">
                <Option value={null}>None</Option>
                <Option value="currentAndRandomYear">CURRENT AND RANDOM YEAR</Option>
              </Select>
            </Col>
          </Row>
        )}
      </Form>
    </Drawer>
  );
};
